import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { postPatientDeposit } from "../services/Handlers/PatientDepositService";
import { useAuth } from "../context/AuthProvider";
import { useNotification } from "../context/NotificationProvider";
import {
  parsePatientDob,
  formatPatientDobForApi,
} from "../utils/patientDob";
import { useHistory } from "react-router-dom";

export const useDepositPatientForm = () => {
  const { userDetail } = useAuth();
  const { error, success } = useNotification();
  const history = useHistory();

  // Patient deposit mutation
  const { mutate: submitPatientDeposit, isLoading: loadingSubmit } =
    useMutation(postPatientDeposit, {
      onSuccess: (response) => {
        if (response.status === 200 || response.status === 201) {
          success("Patient deposit created successfully!");
          setFormData({
            items: null,
            price: "",
            method: "",
            cardNumber: "",
            name: "",
            phoneNumber: "",
            email: "",
            gender: "",
            dateOfBirth: "",
            address: "",
            identificationNumber: "",
            identificationType: "KTP",
            remark: "",
            medicalRecordNumber: "",
            allergies: [],
          });
          setSelectedExistingPatient(null);
          setActiveTab("1");

          history.goBack();
        } else {
          error("Failed to create patient deposit. Please try again.");
        }
      },
      onError: (err) => {
        console.error("DY: Error creating patient deposit:", err);
        error(
          err?.response?.data?.message ||
            "Failed to create patient deposit. Please try again."
        );
      },
    });

  // Patient selection state
  const [selectedExistingPatient, setSelectedExistingPatient] = useState(null);
  const [activeTab, setActiveTab] = useState("1");

  // Combined form state for deposit and patient
  const [formData, setFormData] = useState({
    // Deposit fields
    items: null,
    price: "",
    method: "",
    cardNumber: "",

    // Patient fields
    name: "",
    phoneNumber: "",
    email: "",
    gender: "",
    dateOfBirth: "",
    address: "",
    identificationNumber: "",
    identificationType: "KTP",
    remark: "",
    medicalRecordNumber: "",
    allergies: [],
  });

  // Handle any form field change
  const handleFormChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Get deposit data
  const getDepositData = () => ({
    price: parseFloat(formData.price) || 0,
    method: formData.method,
    cardNumber: formData.cardNumber || null,
    createdBy: userDetail?.firstName || userDetail?.name,
    updatedBy: userDetail?.firstName || userDetail?.name,
    businessId: userDetail?.businessId,
  });

  // Get patient data
  const getPatientData = () => {
    const isExistingPatient = activeTab === "1" && selectedExistingPatient;
    const alergiesDTOs =
      formData.allergies.length > 0
        ? formData.allergies.map((item) => ({
            ...item,
            ...(item.__isNew__ ? {} : { __isNew__: false }),
          }))
        : [];

    return {
      name: isExistingPatient ? selectedExistingPatient.name : formData.name,
      phoneNumber: isExistingPatient
        ? selectedExistingPatient.phoneNumber
        : formData.phoneNumber,
      email: isExistingPatient
        ? selectedExistingPatient?.email || null
        : formData.email || null,
      gender: isExistingPatient
        ? selectedExistingPatient.gender
        : formData.gender,
      dateOfBirth:
        isExistingPatient && selectedExistingPatient.dob
          ? formatPatientDobForApi(
              parsePatientDob(selectedExistingPatient.dob).toDate()
            )
          : formData.dateOfBirth,
      address: isExistingPatient
        ? selectedExistingPatient.address
        : formData.address,
      remark: isExistingPatient
        ? selectedExistingPatient.remark || null
        : formData.remark || null,
      medicalRecordNumber: isExistingPatient
        ? selectedExistingPatient.medicalRecordNumber || null
        : formData.medicalRecordNumber || null,
      identificationNumber: isExistingPatient
        ? selectedExistingPatient.identificationNumber
        : formData.identificationNumber,
      identificationType: isExistingPatient
        ? selectedExistingPatient.identificationType
        : formData.identificationType,
      alergy: alergiesDTOs,
    };
  };

  const handleSubmit = (values) => {
    if (activeTab === "1" && !selectedExistingPatient) {
      error("Please select an existing patient before submitting.");
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      error("Please enter a valid price.");
      return;
    }

    const depositData = getDepositData();
    const patientData = getPatientData();

    let submitData;

    if (activeTab === "1") {
      // Submit with existing patient
      submitData = {
        ...depositData,
        patientData,
        patientId: selectedExistingPatient.id,
        patientType: "existing",
      };
    } else {
      // Submit with new patient
      submitData = {
        ...depositData,
        patientData,
        patientType: "new",
      };
    }

    // Call the API using useMutation
    submitPatientDeposit(submitData);
  };

  return {
    // Data
    formData,
    selectedExistingPatient,
    activeTab,
    loadingSubmit,

    // Setters
    setSelectedExistingPatient,
    setActiveTab,

    // Handlers
    handleFormChange,
    handleSubmit,

    // Getters
    getDepositData,
    getPatientData,
  };
};
